/**
 * How tall things are DRAWN, measured from their own models - what the cursor's line of sight meets
 * (input/aimVolume.ts). The sim's collider heights are about sight lines and cover, not about what the player
 * sees on screen; a watchtower's cabin is drawn 7.4 m up and a cursor on it has to count as the tower.
 * Built once per kind and cached: a model is only measured, never shown.
 */

import { Box3 } from 'three';
import { Pcg32 } from '../../sim/rng';
import type { EnemyKind, PropKind } from '../../sim/types';
import type { Palette } from '../palette';
import { machineModel } from './machineModels';
import { buildPropModel } from './propModels';

const props = new Map<PropKind, number>();
const machines = new Map<EnemyKind, number>();

/** Top of a prop kind's model above its base, metres, at scale 1 (multiply by the prop's own scale). */
export function propDrawnHeight(kind: PropKind, palette: Palette): number {
  const known = props.get(kind);
  if (known !== undefined) return known;
  const model = buildPropModel({ id: 0, kind, x: 0, z: 0, yaw: 0, length: 1.5 }, palette, new Pcg32(1, 1));
  const geometry = model.build();
  const height = Math.max(0.3, (geometry.boundingBox ?? new Box3()).max.y);
  geometry.dispose();
  props.set(kind, height);
  return height;
}

/** Top of a machine's or a vehicle's model, metres. */
export function machineDrawnHeight(kind: EnemyKind, palette: Palette): number {
  const known = machines.get(kind);
  if (known !== undefined) return known;
  const model = machineModel(kind).body(palette, new Pcg32(1, 1));
  const geometry = model.build();
  const height = Math.max(0.5, (geometry.boundingBox ?? new Box3()).max.y);
  geometry.dispose();
  machines.set(kind, height);
  return height;
}
